import React, {
  useEffect,
  useRef,
  useReducer,
  useCallback,
  Ref,
  FormEvent,
  Dispatch,
  ReactNode,
  SetStateAction,
} from 'react'
import Downshift from 'downshift'
import { InputGroup, Menu, MenuItem } from '@blueprintjs/core'
import { Toaster, Icon, IconButton } from '@ui'
import { debounce } from 'lodash'
import { useIntersect } from '@hooks'
import { IConnectionWithTotal } from '@api'

export type ItemProps<T> = {
  skeleton?: boolean
  data?: T
  query?: string
  refItem?: Ref<HTMLDivElement> | Dispatch<SetStateAction<ReactNode>>
}

export type ListFuc<T> = ({
  query,
  after,
}: {
  query: string
  after?: string
}) => Promise<IConnectionWithTotal<T>>

export interface CatalogProps<T> {
  onSelect: (item: T | null) => void
  itemDisabled?: (item: T) => boolean
}

export interface GenericCatalogProps<T> extends CatalogProps<T> {
  Item: React.FC<React.PropsWithChildren<ItemProps<T>>>
  list: ListFuc<T>
}

export default function GenericCatalogWithInfiniteScroll<T extends { id: string }>({
  list,
  onSelect,
  Item,
  itemDisabled,
}: GenericCatalogProps<T>) {
  const hook = useCatalogWithInfiniteScroll<T>(list)
  const { refItem, refRoot } = useIntersect({
    threshold: [0.75],
    callback: hook.next,
  })
  const renderItems = hook.status === 'idle' || hook.status === 'paging'
  const renderNoResults = hook.status === 'idle' && !hook.connection.counters.total

  return (
    <Downshift itemToString={() => ''} onSelect={(item) => onSelect(item)}>
      {({ getInputProps, getMenuProps, getItemProps, highlightedIndex }) => (
        <div
          className="relative overflow-y-auto h-full"
          ref={refRoot}
          style={{
            borderRadius: 4,
            minWidth: 300,
          }}
        >
          <div className="sticky top-0 pb-0 bg-white z-10" style={{ padding: '5px 5px 0 5px' }}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <InputGroup
              autoFocus
              {...getInputProps({
                value: hook.query,
                onChange: ({ currentTarget: { value } }: FormEvent<HTMLInputElement>) => {
                  hook.run(value)
                },
                placeholder: 'Search...',
                leftIcon: 'search',
                rightElement: hook.query && (
                  <IconButton
                    minimal
                    iconName="times"
                    onClick={() => {
                      hook.run('')
                    }}
                  />
                ),
                onFocus: ({ currentTarget }: FormEvent<HTMLInputElement>) => {
                  currentTarget.select()
                },
              })}
            />
          </div>
          <div {...getMenuProps()}>
            <Menu>
              {/* Render items */}
              {renderItems &&
                hook.connection.edges.map(({ node }, i) => (
                  <MenuItem
                    {...getItemProps({
                      key: node.id,
                      item: node,
                      disabled: itemDisabled?.(node),
                    })}
                    active={i === highlightedIndex}
                    text={
                      <Item
                        data={node}
                        query={hook.query}
                        refItem={i === hook.connection.edges.length - 1 ? refItem : undefined}
                      />
                    }
                  />
                ))}
              {/* Render no results */}
              {renderNoResults && <MenuItem disabled text="No results" />}
              {/* Render skeleton */}
              <Skeleton {...{ hook, Item }} />
            </Menu>
          </div>
        </div>
      )}
    </Downshift>
  )
}

function Skeleton<T>({
  hook,
  Item,
}: {
  hook: Hook<T>
  Item: React.FC<React.PropsWithChildren<ItemProps<T>>>
}) {
  let skeletonSize = 0

  switch (hook.status) {
    case 'loading':
      skeletonSize = 7
      break

    case 'filtering':
      skeletonSize = Math.min(hook.connection.edges.length) || 1
      break

    case 'paging':
      skeletonSize = Math.min(hook.connection.counters.total - hook.connection.edges.length, 7)
      break
  }

  return (
    <>
      {Array.from({ length: skeletonSize }).map((_, i) => (
        <MenuItem key={i} text={<Item skeleton />} />
      ))}
    </>
  )
}

interface SearchAction {
  type: 'search'
  query: string
}

interface LoadAction<T> {
  type: 'load'
  connection: IConnectionWithTotal<T>
}

interface ActiveItemUpdateAction<T> {
  type: 'activeItemUpdate'
  activeItem: T | null
}

interface PageAction {
  type: 'page'
}

type Action<T> = SearchAction | LoadAction<T> | PageAction | ActiveItemUpdateAction<T>

interface State<T> {
  status: string
  query: string
  after: string
  connection: IConnectionWithTotal<T>
  activeItem: T | null
}

interface Hook<T> extends State<T> {
  setActiveItem: (acitveItem: T | null) => void
  run: (query: string) => void
  next: () => void
}

export function useCatalogWithInfiniteScroll<T extends { id: string }>(list: ListFuc<T>): Hook<T> {
  const reqId = useRef(0)
  const [state, dispatch] = useReducer(reducer, {
    status: 'loading',
    query: '',
    after: '',
    connection: {
      counters: { total: 0 },
      pageInfo: {
        hasNextPage: false,
        endCursor: '',
      },
      edges: [],
    },
    activeItem: null,
  }) as unknown as [State<T>, Dispatch<Action<T>>]

  const debouncedList = useCallback(
    debounce((params: { query: string; after: string }, newReqId: number) => {
      list(params)
        .then((connection) => {
          if (reqId.current === newReqId) {
            dispatch({
              type: 'load',
              connection,
            })
          }
        })
        .catch((error) => Toaster.fail({ error }))
    }, 300),
    []
  )

  useEffect(() => {
    const newReqId = Date.now()
    reqId.current = newReqId
    debouncedList({ query: state.query, after: state.after }, newReqId)
    return () => debouncedList.cancel()
  }, [state.query, state.after, debouncedList])

  return {
    ...state,
    run(query: string) {
      dispatch({ type: 'search', query })
    },
    next() {
      if (state.connection.pageInfo.hasNextPage && state.status === 'idle') {
        dispatch({ type: 'page' })
      }
    },
    setActiveItem(activeItem: T | null) {
      dispatch({ type: 'activeItemUpdate', activeItem })
    },
  }
}

function reducer<T>(state: State<T>, action: Action<T>) {
  switch (action.type) {
    case 'search':
      return {
        ...state,
        status: 'filtering',
        query: action.query,
        after: '',
      }

    case 'load':
      return {
        ...state,
        status: 'idle',
        connection: state.after
          ? {
              ...action.connection,
              edges: [...state.connection.edges, ...action.connection.edges],
            }
          : action.connection,
      }

    case 'page':
      return {
        ...state,
        status: 'paging',
        after: state.connection.pageInfo.endCursor,
      }

    case 'activeItemUpdate':
      return {
        ...state,
        activeItem: action.activeItem,
      }

    default:
      return state
  }
}
